// What the Hardware tab of a machine shows, worked out from the hardware fact (GET v3/facts?capability=hardware: the latest result of the system
// info script), so the page only lays it out: the computer, how old it is, whether it should be replaced and why. Copied from V2's deviceInfo.js.

const list = (v) => (Array.isArray(v) ? v : []);
const has = (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
const text = (v) => String(v).trim();
const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : null);

// [{label, value}] without the ones the machine did not report.
const facts = (pairs) => pairs.filter(([, value]) => has(value)).map(([label, value]) => ({ label, value: Array.isArray(value) ? value.join(', ') : text(value) }));

export const computerFacts = (r) => {
  const x = r || {};
  const os = [x.OperatingSystem, x.WindowsDisplayVersion].filter(has).join(' ');
  const domain = has(x.Domain) ? `${x.Domain}${x.PartOfDomain === true ? ' (joined)' : x.PartOfDomain === false ? ' (not joined)' : ''}` : null;
  const memory = typeof x.MemoryInstalledGB === 'number' ? `${x.MemoryInstalledGB} GB${typeof x.MemoryUsableGB === 'number' && x.MemoryUsableGB !== x.MemoryInstalledGB ? ` (${x.MemoryUsableGB} GB usable)` : ''}` : null;
  const bios = [x.BiosName, x.BiosVersion].filter(has).join(' ');
  return facts([
    ['Operating system', os], ['Windows version', x.WindowsVersion], ['Architecture', x.Architecture], ['Installation', x.WindowsInstallationType],
    ['Last user', x.LastUser], ['Domain', domain], ['Manufacturer', x.Manufacturer], ['Model', x.Model], ['Serial number', x.SerialNumber],
    ['Processor', x.Processor], ['Cores', x.TotalPhysicalCores], ['Memory', memory], ['BIOS', bios], ['BIOS released', x.BiosReleaseDate],
    ['Antivirus', x.AntivirusProduct], ['Firewall on', yesNo(x.WindowsFirewallEnabled)], ['.NET Framework', x.DotNetVersion], ['Graphics', x.DisplayAdapters],
    ['Reported', x.ReportDate],
  ]);
};

export const VERDICTS = {
  replaceNow: { label: 'Replace now', tone: 'err' },
  planReplacement: { label: 'Plan a replacement', tone: 'warn' },
  healthy: { label: 'Healthy', tone: 'ok' },
};
export const SEVERITIES = { hard: { label: 'Replace now', tone: 'err', rank: 0 }, strong: { label: 'Plan', tone: 'warn', rank: 1 }, soft: { label: 'Tips the balance', tone: 'none', rank: 2 } };

// The age and replacement part: null when the machine has not reported it (a result from before it was added has only the computer's details).
export const replacement = (r) => {
  const a = (r || {}).ReplacementAssessment;
  if (!a || typeof a !== 'object') return null;
  const age = (r || {}).DeviceAge || {};
  const dates = a.dates || {};
  const m = a.metrics || {};
  const drives = list(m.drives).map((d) => [d.model || `Drive ${d.number}`, [d.mediaType, d.isSystem ? 'system drive' : null, d.health, typeof d.wearPercent === 'number' ? `${d.wearPercent}% worn` : null].filter(has).join(', ')]);
  return {
    verdict: VERDICTS[a.verdict] || { label: has(a.verdict) ? text(a.verdict) : 'Not assessed', tone: 'none' },
    reasons: list(a.verdictReasons).map(text),
    findings: list(a.findings).filter((f) => f && has(f.issue)).sort((p, q) => (SEVERITIES[p.severity]?.rank ?? 9) - (SEVERITIES[q.severity]?.rank ?? 9)),
    facts: facts([
      ['Chassis', a.chassis], ['Estimated age', typeof age.estimatedAgeYears === 'number' ? `${age.estimatedAgeYears} years${age.ageSource ? ` (from ${age.ageSource === 'serial' ? 'the serial number' : 'the first Windows install'})` : ''}` : null],
      ['In service since', dates.inServiceDate], ['Age limit', typeof dates.ageLimitYears === 'number' ? `${dates.ageLimitYears} years` : null], ['Replacement due', dates.replacementDueDate],
      ['Windows 11', m.windows11 ? (m.windows11.onWindows11 ? 'Running it' : m.windows11.eligible ? 'Eligible' : 'Not eligible') : null],
      ['System drive', m.systemDrive && typeof m.systemDrive.freePercent === 'number' ? `${m.systemDrive.freeGB} of ${m.systemDrive.sizeGB} GB free (${m.systemDrive.freePercent}%)` : null],
      ['Battery health', m.battery && typeof m.battery.healthPercent === 'number' ? `${m.battery.healthPercent}%` : null],
      ['Unexpected shutdowns', m.stability && typeof m.stability.unexpectedShutdowns === 'number' ? `${m.stability.unexpectedShutdowns} in ${m.stability.days} days` : null],
      ['Blue screens', m.stability && typeof m.stability.blueScreens === 'number' ? `${m.stability.blueScreens} in ${m.stability.days} days` : null],
      ['Median boot', m.boot && typeof m.boot.medianSeconds === 'number' ? `${m.boot.medianSeconds} seconds` : null],
      ...drives,
    ]),
    notRun: list((r || {}).ChecksNotRun).map((c) => `${c.check}: ${c.reason}`),
  };
};
